import React, { useEffect, useState, useRef } from 'react';
import { auth, db } from '../firebase';
import {
  collection, addDoc, onSnapshot, query,
  orderBy, serverTimestamp, doc, setDoc,
} from 'firebase/firestore';

export default function Messages() {
  const name      = localStorage.getItem('ericgcuts_name') || 'Client';
  const phone     = auth.currentUser?.phoneNumber || '';
  const clientId  = phone.replace(/\D/g, '');

  const [messages, setMessages] = useState([]);
  const [text,     setText]     = useState('');
  const [sending,  setSending]  = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (!clientId) return;

    // Make sure conversation doc exists
    setDoc(doc(db, 'conversations', clientId), {
      clientName:  name,
      clientPhone: phone,
      lastMessage:   '',
      lastMessageAt: serverTimestamp(),
      unread: 0,
    }, { merge: true });

    // Listen to messages
    const q = query(
      collection(db, 'conversations', clientId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }), 100);
    });
    return unsub;
  }, [clientId, name, phone]);

  async function send() {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setText('');
    try {
      await addDoc(collection(db, 'conversations', clientId, 'messages'), {
        text:      trimmed,
        sender:    'client',
        createdAt: serverTimestamp(),
      });
      await setDoc(doc(db, 'conversations', clientId), {
        clientName:    name,
        clientPhone:   phone,
        lastMessage:   trimmed,
        lastMessageAt: serverTimestamp(),
        unread:        1,
      }, { merge: true });
    } catch (e) {
      console.error(e);
      setText(trimmed);
    } finally {
      setSending(false);
    }
  }

  function formatTime(ts) {
    if (!ts?.toDate) return '';
    return ts.toDate().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px', background: 'var(--card)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 20,
          background: 'var(--accent-dim)', border: '2px solid var(--accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 800, color: 'var(--accent)',
        }}>E</div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>Eric G Cuts</div>
          <div style={{ fontSize: 12, color: 'var(--success)' }}>● Online</div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={listRef}
        style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 0', display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--subtext)', paddingTop: 60, fontSize: 15 }}>
            Send Eric a message — ask about availability, styles, or anything else 👋
          </div>
        )}
        {messages.map(msg => {
          const isEric = msg.sender === 'eric';
          return (
            <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isEric ? 'flex-start' : 'flex-end' }}>
              <div style={{
                maxWidth: '78%', padding: '10px 14px', borderRadius: 18,
                borderBottomLeftRadius:  isEric ? 4 : 18,
                borderBottomRightRadius: isEric ? 18 : 4,
                background: isEric ? 'var(--card)' : 'var(--accent)',
                border: isEric ? '1px solid var(--border)' : 'none',
              }}>
                <div style={{ fontSize: 15, lineHeight: 1.4, color: isEric ? 'var(--text)' : '#000', fontWeight: isEric ? 400 : 600 }}>
                  {msg.text}
                </div>
                <div style={{ fontSize: 11, marginTop: 4, color: isEric ? 'var(--subtext)' : 'rgba(0,0,0,0.5)', textAlign: isEric ? 'left' : 'right' }}>
                  {formatTime(msg.createdAt)}
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ height: 16 }} />
      </div>

      {/* Input */}
      <div style={{
        padding: '10px 12px', background: 'var(--card)',
        borderTop: '1px solid var(--border)',
        display: 'flex', gap: 10, alignItems: 'flex-end',
        paddingBottom: 80, // nav bar height
      }}>
        <textarea
          style={{
            flex: 1, background: 'var(--input-bg)', borderRadius: 20,
            border: 'none', padding: '10px 16px',
            color: 'var(--text)', fontSize: 15, resize: 'none',
            maxHeight: 120, outline: 'none', fontFamily: 'inherit', lineHeight: 1.4,
          }}
          placeholder="Message Eric…"
          value={text}
          onChange={e => setText(e.target.value)}
          rows={1}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
        />
        <button
          onClick={send}
          disabled={!text.trim() || sending}
          style={{
            width: 42, height: 42, borderRadius: 21,
            background: 'var(--accent)', border: 'none',
            fontSize: 20, color: '#000', fontWeight: 900,
            opacity: (!text.trim() || sending) ? 0.35 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >↑</button>
      </div>
    </div>
  );
}
